import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ClockSession, UseClockSessionsOptions, UseClockSessionsResult } from '../types';
import { CLOCK_SESSIONS_KEY } from '../constants/dashboardConstants';
import { getTodayDateString } from '../utils/dashboardUtils';

// Load sessions from localStorage
const loadSessions = (): ClockSession[] => {
  try {
    const stored = localStorage.getItem(CLOCK_SESSIONS_KEY);
    if (!stored) return [];
    const sessions = JSON.parse(stored);
    return sessions.map((s: any) => ({
      ...s,
      clockInTime: new Date(s.clockInTime),
      clockOutTime: s.clockOutTime ? new Date(s.clockOutTime) : null
    }));
  } catch {
    return [];
  }
};

// Save sessions to localStorage
const saveSessions = (sessions: ClockSession[]) => {
  localStorage.setItem(CLOCK_SESSIONS_KEY, JSON.stringify(sessions));
};

/**
 * Hook to manage clock in/out sessions with localStorage persistence
 */
export const useClockSessions = ({
  isClockedIn,
  elapsedSeconds,
  onClockIn,
  onClockOut
}: UseClockSessionsOptions): UseClockSessionsResult => {
  const [sessions, setSessions] = useState<ClockSession[]>(() => loadSessions());
  const [currentSessionStart, setCurrentSessionStart] = useState<Date | null>(null);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const prevClockedInRef = useRef<boolean | null>(null);

  // Track clock state transitions
  useEffect(() => {
    // Detect clock-in (transition from not clocked in to clocked in)
    if (isClockedIn && prevClockedInRef.current === false) {
      setCurrentSessionStart(new Date());
    }
    
    // Detect clock-out (transition from clocked in to not clocked in)
    if (!isClockedIn && prevClockedInRef.current === true && currentSessionStart) {
      const now = new Date();
      const duration = Math.floor((now.getTime() - currentSessionStart.getTime()) / 1000);
      
      const newSession: ClockSession = {
        id: `session-${Date.now()}`,
        clockInTime: currentSessionStart,
        clockOutTime: now,
        duration
      };
      
      setSessions(prev => {
        const updated = [newSession, ...prev];
        saveSessions(updated);
        return updated;
      });
      setCurrentSessionStart(null);
    }
    
    prevClockedInRef.current = isClockedIn;
  }, [isClockedIn, currentSessionStart]);

  // Filter today's sessions
  const todaySessions = useMemo(() => {
    const today = getTodayDateString();
    return sessions.filter(session => {
      const sessionDate = session.clockInTime.toISOString().split('T')[0];
      return sessionDate === today;
    });
  }, [sessions]);

  // Calculate total time today
  const totalTimeToday = useMemo(() => {
    const completedTime = todaySessions.reduce((acc, session) => acc + session.duration, 0);
    return completedTime + (isClockedIn ? elapsedSeconds : 0);
  }, [todaySessions, isClockedIn, elapsedSeconds]);

  // Handle clock in with session tracking
  const handleClockIn = useCallback(async () => {
    const success = await onClockIn();
    if (success) {
      setCurrentSessionStart(new Date());
    }
  }, [onClockIn]);

  // Handle clock out with session tracking
  const handleClockOut = useCallback(async () => {
    const clockOutTime = new Date();
    const success = await onClockOut();
    if (success && currentSessionStart) {
      const duration = Math.floor((clockOutTime.getTime() - currentSessionStart.getTime()) / 1000);
      
      const newSession: ClockSession = {
        id: `session-${Date.now()}`,
        clockInTime: currentSessionStart,
        clockOutTime: clockOutTime,
        duration
      };
      
      setSessions(prev => {
        const updated = [newSession, ...prev];
        saveSessions(updated);
        return updated;
      });
      setCurrentSessionStart(null);
    }
  }, [onClockOut, currentSessionStart]);

  return {
    sessions,
    todaySessions,
    currentSessionStart,
    totalTimeToday,
    handleClockIn,
    handleClockOut,
    showSessionHistory,
    setShowSessionHistory
  };
};

export default useClockSessions;

